import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Send, MessagesSquare, CheckCircle2 } from "lucide-react";
import api, { BACKEND_URL, getToken } from "../lib/api";
import { extractError } from "../lib/formatError";

const POLL_MS = 5000;
const WS_RETRY_MS = 4000;

export default function AdminChat() {
  const [convs, setConvs] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState("open");
  const [hasUnread, setHasUnread] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [wsConnected, setWsConnected] = useState(false);
  const bottomRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const loadList = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status, has_unread: String(hasUnread), limit: "50" });
      const { data } = await api.get(`/admin/chat/conversations?${params}`);
      setConvs(data.items);
      setTotal(data.total);
    } catch (e) { setError(extractError(e)); }
  }, [status, hasUnread]);

  const loadDetail = useCallback(async (id) => {
    if (!id) return;
    try {
      const { data } = await api.get(`/admin/chat/conversations/${id}`);
      setDetail(data);
    } catch (e) { setError(extractError(e)); }
  }, []);

  useEffect(() => {
    loadList();
    pollRef.current = setInterval(loadList, POLL_MS);
    return () => clearInterval(pollRef.current);
  }, [loadList]);
  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [detail?.messages?.length]);

  // Admin WS
  useEffect(() => {
    let cancelled = false;
    let retry;
    const connect = () => {
      if (cancelled) return;
      const proto = window.location.protocol === "https:" ? "wss" : "ws";
      const host = BACKEND_URL.replace(/^https?:\/\//, "");
      const url = `${proto}://${host}/ws/chat?token=${encodeURIComponent(getToken() || "")}`;
      const ws = new WebSocket(url);
      wsRef.current = ws;
      ws.onopen = () => setWsConnected(true);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === "message") {
            loadList();
            if (data.conversation && data.conversation.id === selectedId) loadDetail(selectedId);
          }
        } catch (e) {}
      };
      ws.onclose = () => {
        setWsConnected(false);
        wsRef.current = null;
        if (!cancelled) retry = setTimeout(connect, WS_RETRY_MS);
      };
    };
    connect();
    return () => { cancelled = true; if (retry) clearTimeout(retry); if (wsRef.current) try { wsRef.current.close(); } catch {} };
    // Intentionally only re-connect on selectedId change. `loadList`/`loadDetail`
    // are used inside ws.onmessage but including them would tear the socket
    // down on every filter change, which is worse than a slightly stale closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const send = async (e) => {
    e.preventDefault();
    if (!selectedId || !text.trim() || busy) return;
    setBusy(true); setError(null);
    try {
      const { data } = await api.post(`/admin/chat/conversations/${selectedId}/messages`, { body: text.trim() });
      setDetail((d) => d ? { ...d, messages: [...d.messages, data.message] } : d);
      setText("");
      loadList();
    } catch (e2) { setError(extractError(e2)); } finally { setBusy(false); }
  };

  const closeConv = async () => {
    if (!selectedId) return;
    if (!confirm("Close this conversation?")) return;
    try {
      await api.post(`/admin/chat/conversations/${selectedId}/close`);
      toast.success("Conversation closed");
      loadList(); loadDetail(selectedId);
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="admin-chat">
      <Toaster theme="dark" position="top-center" />
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] tracking-[0.4em] uppercase text-[color:var(--muted)]">Chat</div>
          <h1 className="font-display italic text-3xl text-[color:var(--ink)]">Support inbox ({total})</h1>
        </div>
        <div className="flex items-center gap-2 text-[10px] tracking-widest uppercase">
          <div className={`h-2 w-2 rounded-full ${wsConnected ? "bg-emerald-400" : "bg-yellow-500"}`} />
          <span className="text-[color:var(--muted)]">{wsConnected ? "Live" : "Polling"}</span>
        </div>
      </div>

      <div className="flex gap-2 mb-3 text-[10px] tracking-widest uppercase">
        {["open", "closed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`px-3 py-1.5 border ${status === s ? "border-[color:var(--gold)] text-[color:var(--gold)]" : "border-[color:var(--line)] text-[color:var(--muted)]"}`} data-testid={`chat-tab-${s}`}>{s}</button>
        ))}
        <label className="ml-2 flex items-center gap-2 text-[color:var(--muted)] cursor-pointer">
          <input type="checkbox" checked={hasUnread} onChange={(e) => setHasUnread(e.target.checked)} data-testid="chat-filter-unread" />
          Unread only
        </label>
      </div>

      {error && <div className="text-xs text-[color:var(--red)] border border-[color:var(--red)] px-3 py-2 mb-3">{error}</div>}

      <div className="flex-1 flex gap-3 min-h-0">
        {/* Left: conversation list */}
        <aside className="w-80 border overflow-y-auto" style={{ borderColor: "var(--line)", background: "var(--card)" }}>
          {convs.length === 0 ? (
            <div className="text-xs text-[color:var(--muted)] italic p-4">Nothing here.</div>
          ) : convs.map((c) => (
            <button
              key={c.id}
              onClick={() => setSelectedId(c.id)}
              className={`w-full text-left px-4 py-3 border-b block ${selectedId === c.id ? "bg-[color:var(--shell-2)] border-l-2 border-l-[color:var(--gold)]" : ""}`}
              style={{ borderBottomColor: "var(--line)" }}
              data-testid={`conv-${c.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-sm text-[color:var(--ink)] truncate flex-1">{c.user_email || c.user_id.slice(0, 10)}</div>
                {c.unread_admin_count > 0 && (
                  <span className="text-[9px] uppercase tracking-widest px-1.5 rounded-full ml-2" style={{ background: "var(--red)", color: "white" }} data-testid={`unread-${c.id}`}>
                    {c.unread_admin_count}
                  </span>
                )}
              </div>
              <div className="text-[10px] text-[color:var(--muted)] truncate mt-1">
                {c.last_message_preview || "No messages yet"}
              </div>
              <div className="text-[9px] tracking-widest text-[color:var(--muted)] mt-0.5">
                {c.last_message_at ? new Date(c.last_message_at).toLocaleString() : ""}
              </div>
            </button>
          ))}
        </aside>

        {/* Right: detail */}
        <section className="flex-1 flex flex-col border" style={{ borderColor: "var(--line)", background: "var(--card)" }}>
          {!detail ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-2 text-[color:var(--muted)]">
              <MessagesSquare className="w-8 h-8 opacity-40" />
              <div className="text-xs tracking-widest uppercase">Select a conversation</div>
            </div>
          ) : (
            <>
              <header className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--line)" }}>
                <div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--muted)]">{detail.conversation.status}</div>
                  <div className="text-sm text-[color:var(--ink)]">{detail.conversation.user_email}</div>
                </div>
                {detail.conversation.status !== "closed" && (
                  <button onClick={closeConv} className="text-[10px] tracking-widest uppercase text-[color:var(--muted)] hover:text-[color:var(--red)]" data-testid="conv-close-btn">
                    Close
                  </button>
                )}
              </header>
              <div className="flex-1 overflow-y-auto p-4 space-y-2" data-testid="conv-messages">
                {detail.messages.map((m) => (
                  <div key={m.id} className={`flex ${m.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[70%] px-3 py-2 text-xs whitespace-pre-wrap border" style={{ background: m.sender_type === "admin" ? "rgba(212,161,58,0.1)" : "var(--shell-2)", borderColor: m.sender_type === "admin" ? "var(--gold)" : "var(--line)" }}>
                      <div className="text-[color:var(--ink)]">{m.body}</div>
                      <div className="text-[9px] tracking-widest uppercase text-[color:var(--muted)] mt-1">
                        {m.sender_type} · {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>
              {detail.conversation.status !== "closed" && (
                <form onSubmit={send} className="flex items-end gap-2 p-3 border-t" style={{ borderColor: "var(--line)" }}>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value.slice(0, 2000))}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
                    rows={2}
                    placeholder="Reply as support…"
                    className="flex-1 bet-input text-xs resize-none"
                    data-testid="conv-reply-input"
                  />
                  <button type="submit" disabled={!text.trim() || busy} className="p-2 border border-[color:var(--gold)] text-[color:var(--gold)] hover:bg-[color:var(--shell-2)] disabled:opacity-40" data-testid="conv-reply-btn">
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
}
